import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Key,
  Webhook,
  Copy,
  Eye,
  EyeOff,
  Plus,
  Trash2,
  RefreshCw,
  ExternalLink,
  CheckCircle,
  Clock,
  AlertCircle,
  Settings,
  Terminal,
  Book,
  Zap,
  Shield,
  Activity,
  ChevronDown,
  Play,
  X,
  Loader2,
  Send,
  FileJson,
  Download,
  Code,
  FileText,
  Info,
  ArrowRight,
} from 'lucide-react';
import { useThemeStore } from '../store/theme';
import { useAuthStore } from '../store/auth';

const API_URL = import.meta.env.VITE_API_URL || '/api';

// API Response types
interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  message?: string;
}

interface PermissionsResponse {
  permissions: Permission[];
  grouped: Record<string, Permission[]>;
}

interface WebhookEventsResponse {
  events: WebhookEvent[];
  grouped: Record<string, WebhookEvent[]>;
}

interface CreateKeyResponse {
  id: number;
  name: string;
  key: string;
  key_prefix: string;
  permissions: string[];
  expires_at: string | null;
  is_active: boolean;
  created_at: string;
}

interface CreateWebhookResponse {
  id: number;
  url: string;
  events: string[];
  secret: string;
  is_active: boolean;
  created_at: string;
}

interface TestWebhookResponse {
  delivered: boolean;
  status_code: number;
  message: string;
}

// Types
interface ApiKey {
  id: number;
  name: string;
  key?: string;
  key_prefix: string;
  permissions: string[];
  last_used_at: string | null;
  expires_at: string | null;
  is_active: boolean;
  created_at: string;
  request_count: number;
}

interface Permission {
  id: string;
  category: string;
  name: string;
  description: string;
}

interface UserWebhook {
  id: number;
  url: string;
  events: string[];
  secret?: string;
  is_active: boolean;
  last_triggered_at: string | null;
  success_count: number;
  failure_count: number;
  success_rate: number;
  created_at: string;
}

interface WebhookEvent {
  id: string;
  category: string;
  name: string;
  description: string;
}

interface ApiStats {
  total_keys: number;
  active_keys: number;
  total_requests: number;
}

const apiEndpoints = [
  {
    method: 'GET',
    path: '/api/employees',
    description: 'List all AI employees',
    example: `curl -X GET "https://sweetpilot.com/api/employees" \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
  },
  {
    method: 'POST',
    path: '/api/conversations',
    description: 'Start a conversation with an AI employee',
    example: `curl -X POST "https://sweetpilot.com/api/conversations" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"employee_id": "aria", "title": "New conversation"}'`,
  },
  {
    method: 'POST',
    path: '/api/conversations/:id/messages',
    description: 'Send a message to an AI employee',
    example: `curl -X POST "https://sweetpilot.com/api/conversations/1/messages" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"content": "Create a social media post about our new product"}'`,
  },
  {
    method: 'GET',
    path: '/api/tasks',
    description: 'Get all tasks',
    example: `curl -X GET "https://sweetpilot.com/api/tasks" \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
  },
  {
    method: 'POST',
    path: '/api/tasks',
    description: 'Create a new task',
    example: `curl -X POST "https://sweetpilot.com/api/tasks" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"employee_id": "nova", "title": "Support ticket analysis", "description": "Analyze recent tickets"}'`,
  },
  {
    method: 'GET',
    path: '/api/brain/documents',
    description: 'Get knowledge base documents',
    example: `curl -X GET "https://sweetpilot.com/api/brain/documents" \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
  },
];

// Comprehensive API Documentation
const fullApiDocumentation = {
  authentication: {
    title: 'Authentication',
    description: 'All API requests require authentication using a Bearer token. Include your API key in the Authorization header.',
    example: `Authorization: Bearer sp_live_xxxxxxxxxxxx`,
    notes: [
      'API keys start with "sp_live_" for production or "sp_test_" for testing',
      'Keep your API keys secure and never expose them in client-side code',
      'You can create multiple API keys with different permissions',
      'Rotate your keys regularly for enhanced security',
    ],
  },
  baseUrl: 'https://sweetpilot.com/api',
  rateLimiting: {
    title: 'Rate Limiting',
    description: 'API requests are rate limited based on your plan',
    limits: [
      { plan: 'Free', requests: '100 requests/hour' },
      { plan: 'Pro', requests: '1,000 requests/hour' },
      { plan: 'Enterprise', requests: '10,000 requests/hour' },
    ],
    headers: [
      { name: 'X-RateLimit-Limit', description: 'Maximum requests allowed per window' },
      { name: 'X-RateLimit-Remaining', description: 'Requests remaining in current window' },
      { name: 'X-RateLimit-Reset', description: 'Unix timestamp when the window resets' },
    ],
  },
  endpoints: [
    {
      category: 'Employees',
      endpoints: [
        {
          method: 'GET',
          path: '/employees',
          description: 'List all available AI employees',
          permissions: ['employees:read'],
          parameters: [],
          response: `{
  "success": true,
  "data": [
    {
      "id": "aria",
      "name": "Aria",
      "role": "Social Media Manager",
      "description": "Expert in social media strategy and content creation",
      "avatar": "https://sweetpilot.com/employees/aria.png",
      "capabilities": ["content_creation", "scheduling", "analytics"]
    }
  ]
}`,
        },
        {
          method: 'GET',
          path: '/employees/:id',
          description: 'Get details of a specific AI employee',
          permissions: ['employees:read'],
          parameters: [
            { name: 'id', type: 'string', required: true, description: 'Employee ID (e.g., aria, nova, atlas)' }
          ],
          response: `{
  "success": true,
  "data": {
    "id": "aria",
    "name": "Aria",
    "role": "Social Media Manager",
    "description": "Expert in social media strategy and content creation",
    "avatar": "https://sweetpilot.com/employees/aria.png",
    "capabilities": ["content_creation", "scheduling", "analytics"],
    "stats": {
      "conversations": 1250,
      "tasks_completed": 890,
      "avg_response_time": "2.3s"
    }
  }
}`,
        },
      ],
    },
    {
      category: 'Conversations',
      endpoints: [
        {
          method: 'GET',
          path: '/conversations',
          description: 'List all conversations',
          permissions: ['conversations:read'],
          parameters: [
            { name: 'employee_id', type: 'string', required: false, description: 'Filter by employee ID' },
            { name: 'limit', type: 'number', required: false, description: 'Number of results (default: 20, max: 100)' },
            { name: 'offset', type: 'number', required: false, description: 'Offset for pagination' },
          ],
          response: `{
  "success": true,
  "data": [
    {
      "id": 1,
      "employee_id": "aria",
      "title": "Social media campaign",
      "created_at": "2024-01-15T10:30:00Z",
      "updated_at": "2024-01-15T11:45:00Z",
      "message_count": 12
    }
  ],
  "pagination": {
    "total": 45,
    "limit": 20,
    "offset": 0
  }
}`,
        },
        {
          method: 'POST',
          path: '/conversations',
          description: 'Start a new conversation with an AI employee',
          permissions: ['conversations:write'],
          parameters: [
            { name: 'employee_id', type: 'string', required: true, description: 'ID of the AI employee' },
            { name: 'title', type: 'string', required: false, description: 'Conversation title (auto-generated if not provided)' },
            { name: 'initial_message', type: 'string', required: false, description: 'First message to send' },
          ],
          response: `{
  "success": true,
  "data": {
    "id": 123,
    "employee_id": "aria",
    "title": "New conversation",
    "created_at": "2024-01-15T10:30:00Z"
  }
}`,
        },
        {
          method: 'POST',
          path: '/conversations/:id/messages',
          description: 'Send a message in a conversation',
          permissions: ['conversations:write'],
          parameters: [
            { name: 'id', type: 'number', required: true, description: 'Conversation ID' },
            { name: 'content', type: 'string', required: true, description: 'Message content' },
            { name: 'attachments', type: 'array', required: false, description: 'Array of attachment URLs' },
          ],
          response: `{
  "success": true,
  "data": {
    "id": 456,
    "role": "assistant",
    "content": "Here's a social media post for your new product launch...",
    "created_at": "2024-01-15T10:32:00Z",
    "tokens_used": 150
  }
}`,
        },
        {
          method: 'GET',
          path: '/conversations/:id/messages',
          description: 'Get all messages in a conversation',
          permissions: ['conversations:read'],
          parameters: [
            { name: 'id', type: 'number', required: true, description: 'Conversation ID' },
            { name: 'limit', type: 'number', required: false, description: 'Number of messages (default: 50)' },
          ],
          response: `{
  "success": true,
  "data": [
    {
      "id": 1,
      "role": "user",
      "content": "Create a social media post about our new product",
      "created_at": "2024-01-15T10:30:00Z"
    },
    {
      "id": 2,
      "role": "assistant",
      "content": "Here's an engaging post for your product launch...",
      "created_at": "2024-01-15T10:31:00Z"
    }
  ]
}`,
        },
      ],
    },
    {
      category: 'Tasks',
      endpoints: [
        {
          method: 'GET',
          path: '/tasks',
          description: 'List all tasks',
          permissions: ['tasks:read'],
          parameters: [
            { name: 'status', type: 'string', required: false, description: 'Filter by status (pending, in_progress, completed, failed)' },
            { name: 'employee_id', type: 'string', required: false, description: 'Filter by employee ID' },
          ],
          response: `{
  "success": true,
  "data": [
    {
      "id": 1,
      "employee_id": "nova",
      "title": "Analyze support tickets",
      "description": "Review and categorize recent support tickets",
      "status": "completed",
      "result": "Analyzed 50 tickets. 60% billing, 25% technical, 15% general.",
      "created_at": "2024-01-15T09:00:00Z",
      "completed_at": "2024-01-15T09:15:00Z"
    }
  ]
}`,
        },
        {
          method: 'POST',
          path: '/tasks',
          description: 'Create a new task for an AI employee',
          permissions: ['tasks:write'],
          parameters: [
            { name: 'employee_id', type: 'string', required: true, description: 'ID of the AI employee to handle the task' },
            { name: 'title', type: 'string', required: true, description: 'Task title' },
            { name: 'description', type: 'string', required: false, description: 'Detailed task description' },
            { name: 'priority', type: 'string', required: false, description: 'Priority level (low, medium, high)' },
            { name: 'due_date', type: 'string', required: false, description: 'ISO 8601 date string for due date' },
          ],
          response: `{
  "success": true,
  "data": {
    "id": 123,
    "employee_id": "nova",
    "title": "Support ticket analysis",
    "status": "pending",
    "created_at": "2024-01-15T10:00:00Z"
  }
}`,
        },
        {
          method: 'GET',
          path: '/tasks/:id',
          description: 'Get task details and status',
          permissions: ['tasks:read'],
          parameters: [
            { name: 'id', type: 'number', required: true, description: 'Task ID' },
          ],
          response: `{
  "success": true,
  "data": {
    "id": 123,
    "employee_id": "nova",
    "title": "Support ticket analysis",
    "description": "Analyze recent support tickets",
    "status": "completed",
    "result": "Analysis complete. Found 3 critical issues.",
    "created_at": "2024-01-15T10:00:00Z",
    "completed_at": "2024-01-15T10:15:00Z"
  }
}`,
        },
      ],
    },
    {
      category: 'Brain (Knowledge Base)',
      endpoints: [
        {
          method: 'GET',
          path: '/brain/documents',
          description: 'List all documents in the knowledge base',
          permissions: ['brain:read'],
          parameters: [
            { name: 'type', type: 'string', required: false, description: 'Filter by document type (pdf, txt, url, etc.)' },
          ],
          response: `{
  "success": true,
  "data": [
    {
      "id": 1,
      "name": "Product Documentation",
      "type": "pdf",
      "size": 1024000,
      "chunks": 45,
      "created_at": "2024-01-10T08:00:00Z"
    }
  ]
}`,
        },
        {
          method: 'POST',
          path: '/brain/documents',
          description: 'Upload a document to the knowledge base',
          permissions: ['brain:write'],
          parameters: [
            { name: 'file', type: 'file', required: true, description: 'Document file (PDF, TXT, DOCX, etc.)' },
            { name: 'name', type: 'string', required: false, description: 'Document name (defaults to filename)' },
          ],
          response: `{
  "success": true,
  "data": {
    "id": 10,
    "name": "Q1 Report.pdf",
    "type": "pdf",
    "size": 512000,
    "status": "processing",
    "created_at": "2024-01-15T10:00:00Z"
  }
}`,
        },
        {
          method: 'POST',
          path: '/brain/query',
          description: 'Query the knowledge base',
          permissions: ['brain:read'],
          parameters: [
            { name: 'query', type: 'string', required: true, description: 'Search query' },
            { name: 'limit', type: 'number', required: false, description: 'Number of results (default: 5)' },
          ],
          response: `{
  "success": true,
  "data": {
    "results": [
      {
        "document_id": 1,
        "document_name": "Product Documentation",
        "content": "Relevant content snippet...",
        "similarity": 0.92
      }
    ]
  }
}`,
        },
      ],
    },
    {
      category: 'Webhooks',
      endpoints: [
        {
          method: 'GET',
          path: '/user-webhooks',
          description: 'List all configured webhooks',
          permissions: ['webhooks:read'],
          parameters: [],
          response: `{
  "success": true,
  "data": [
    {
      "id": 1,
      "url": "https://your-app.com/webhooks/sweetpilot",
      "events": ["task.completed", "conversation.message"],
      "is_active": true,
      "success_rate": 98.5,
      "created_at": "2024-01-01T00:00:00Z"
    }
  ]
}`,
        },
        {
          method: 'POST',
          path: '/user-webhooks',
          description: 'Create a new webhook endpoint',
          permissions: ['webhooks:write'],
          parameters: [
            { name: 'url', type: 'string', required: true, description: 'Webhook endpoint URL' },
            { name: 'events', type: 'array', required: true, description: 'Array of event types to subscribe to' },
          ],
          response: `{
  "success": true,
  "data": {
    "id": 5,
    "url": "https://your-app.com/webhooks/sweetpilot",
    "events": ["task.completed"],
    "secret": "whsec_xxxxxxxxxxxxxxxxxxxx",
    "is_active": true,
    "created_at": "2024-01-15T10:00:00Z"
  }
}`,
        },
      ],
    },
  ],
  errorCodes: [
    { code: 400, name: 'Bad Request', description: 'Invalid request parameters or body' },
    { code: 401, name: 'Unauthorized', description: 'Missing or invalid API key' },
    { code: 403, name: 'Forbidden', description: 'API key lacks required permissions' },
    { code: 404, name: 'Not Found', description: 'Requested resource does not exist' },
    { code: 429, name: 'Too Many Requests', description: 'Rate limit exceeded' },
    { code: 500, name: 'Internal Server Error', description: 'Server-side error' },
  ],
};

// Generate Postman collection for an API key
const generatePostmanCollection = (apiKey: ApiKey) => {
  const collection = {
    info: {
      name: `SweetPilot API - ${apiKey.name}`,
      description: `Postman collection for SweetPilot API key: ${apiKey.name}`,
      schema: "https://schema.getpostman.com/json/collection/v2.1.0/collection.json",
    },
    auth: {
      type: "bearer",
      bearer: [
        {
          key: "token",
          value: apiKey.key || apiKey.key_prefix + "••••••••••••••••",
          type: "string"
        }
      ]
    },
    variable: [
      {
        key: "baseUrl",
        value: "https://sweetpilot.com/api",
        type: "string"
      }
    ],
    item: fullApiDocumentation.endpoints.map(category => ({
      name: category.category,
      item: category.endpoints.map(endpoint => ({
        name: endpoint.description,
        request: {
          method: endpoint.method,
          header: [
            {
              key: "Content-Type",
              value: "application/json"
            }
          ],
          url: {
            raw: `{{baseUrl}}${endpoint.path}`,
            host: ["{{baseUrl}}"],
            path: endpoint.path.split('/').filter(Boolean),
          },
          body: endpoint.method !== 'GET' ? {
            mode: "raw",
            raw: JSON.stringify(
              endpoint.parameters
                .filter(p => p.required)
                .reduce((acc, p) => ({ ...acc, [p.name]: p.type === 'string' ? 'example' : p.type === 'number' ? 1 : [] }), {}),
              null, 2
            )
          } : undefined,
        },
        response: [],
      })),
    })),
  };
  return collection;
};

type TabType = 'keys' | 'webhooks' | 'docs' | 'playground';

export default function APIPage() {
  const { theme } = useThemeStore();
  const { token } = useAuthStore();
  const isDark = theme === 'dark';
  const [activeTab, setActiveTab] = useState<TabType>('keys');
  const [showApiKey, setShowApiKey] = useState<{ [key: number]: boolean }>({});
  const [showCreateKey, setShowCreateKey] = useState(false);
  const [showCreateWebhook, setShowCreateWebhook] = useState(false);
  const [copiedKey, setCopiedKey] = useState<number | null>(null);
  const [expandedEndpoint, setExpandedEndpoint] = useState<number | null>(null);

  // API State
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [webhooks, setWebhooks] = useState<UserWebhook[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [webhookEvents, setWebhookEvents] = useState<WebhookEvent[]>([]);
  const [stats, setStats] = useState<ApiStats>({ total_keys: 0, active_keys: 0, total_requests: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyPermissions, setNewKeyPermissions] = useState<string[]>([]);
  const [newKeyExpiry, setNewKeyExpiry] = useState<number>(0);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const [newWebhookUrl, setNewWebhookUrl] = useState('');
  const [newWebhookEvents, setNewWebhookEvents] = useState<string[]>([]);
  const [createdWebhookSecret, setCreatedWebhookSecret] = useState<string | null>(null);

  // Key documentation modal
  const [showKeyDocs, setShowKeyDocs] = useState(false);
  const [selectedKeyForDocs, setSelectedKeyForDocs] = useState<ApiKey | null>(null);
  const [docSection, setDocSection] = useState<'overview' | 'endpoints' | 'examples' | 'postman'>('overview');

  // Playground state
  const [playgroundMethod, setPlaygroundMethod] = useState('GET');
  const [playgroundPath, setPlaygroundPath] = useState('/api/employees');
  const [playgroundBody, setPlaygroundBody] = useState('{}');
  const [playgroundResponse, setPlaygroundResponse] = useState<string | null>(null);
  const [playgroundStatus, setPlaygroundStatus] = useState<number | null>(null);
  const [playgroundLoading, setPlaygroundLoading] = useState(false);

  const tabs = [
    { id: 'keys' as TabType, label: 'API Keys', icon: Key },
    { id: 'webhooks' as TabType, label: 'Webhooks', icon: Webhook },
    { id: 'docs' as TabType, label: 'Documentation', icon: Book },
    { id: 'playground' as TabType, label: 'API Playground', icon: Terminal },
  ];

  // Fetch API keys
  const fetchApiKeys = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/keys`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data: ApiResponse<ApiKey[]> = await response.json();
      if (data.success) {
        setApiKeys(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch API keys:', err);
    }
  }, [token]);

  // Fetch permissions
  const fetchPermissions = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/keys/permissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data: ApiResponse<PermissionsResponse> = await response.json();
      if (data.success) {
        setPermissions(data.data.permissions);
      }
    } catch (err) {
      console.error('Failed to fetch permissions:', err);
    }
  }, [token]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/keys/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data: ApiResponse<ApiStats> = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [token]);

  // Fetch webhooks
  const fetchWebhooks = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/user-webhooks`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data: ApiResponse<UserWebhook[]> = await response.json();
      if (data.success) {
        setWebhooks(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch webhooks:', err);
    }
  }, [token]);

  // Fetch webhook events
  const fetchWebhookEvents = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/user-webhooks/events`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data: ApiResponse<WebhookEventsResponse> = await response.json();
      if (data.success) {
        setWebhookEvents(data.data.events);
      }
    } catch (err) {
      console.error('Failed to fetch webhook events:', err);
    }
  }, [token]);

  // Initial fetch
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([
        fetchApiKeys(),
        fetchPermissions(),
        fetchStats(),
        fetchWebhooks(),
        fetchWebhookEvents(),
      ]);
      setLoading(false);
    };
    fetchData();
  }, [fetchApiKeys, fetchPermissions, fetchStats, fetchWebhooks, fetchWebhookEvents]);

  const copyToClipboard = (text: string, keyId: number) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(keyId);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const toggleKeyVisibility = (keyId: number) => {
    setShowApiKey((prev) => ({ ...prev, [keyId]: !prev[keyId] }));
  };

  const maskApiKey = (key: string) => {
    return key.substring(0, 12) + '••••••••••••••••';
  };

  const getMethodColor = (method: string) => {
    switch (method) {
      case 'GET':
        return 'bg-green-500/20 text-green-400';
      case 'POST':
        return 'bg-blue-500/20 text-blue-400';
      case 'PUT':
        return 'bg-yellow-500/20 text-yellow-400';
      case 'DELETE':
        return 'bg-red-500/20 text-red-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  const formatDate = (date: string | null) => {
    if (!date) return 'Never';
    const d = new Date(date);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
    return d.toLocaleDateString();
  };

  // Create API key
  const createApiKey = async () => {
    if (!newKeyName.trim()) {
      setError('Please enter a key name');
      return;
    }
    if (newKeyPermissions.length === 0) {
      setError('Please select at least one permission');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/keys`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: newKeyName,
          permissions: newKeyPermissions,
          expires_in_days: newKeyExpiry || null,
        }),
      });

      const data: ApiResponse<CreateKeyResponse> = await response.json();
      if (data.success) {
        setCreatedKey(data.data.key);
        await fetchApiKeys();
        await fetchStats();
      } else {
        setError(data.error || 'Failed to create API key');
      }
    } catch (err) {
      setError('Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  // Delete API key
  const deleteApiKey = async (keyId: number) => {
    if (!confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/keys/${keyId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data: ApiResponse<null> = await response.json();
      if (data.success) {
        await fetchApiKeys();
        await fetchStats();
      }
    } catch (err) {
      console.error('Failed to delete API key:', err);
    }
  };

  // Regenerate API key
  const regenerateApiKey = async (keyId: number) => {
    if (!confirm('Are you sure you want to regenerate this API key? The old key will stop working immediately.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/keys/${keyId}/regenerate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data: ApiResponse<CreateKeyResponse> = await response.json();
      if (data.success) {
        setCreatedKey(data.data.key);
        setShowCreateKey(true);
        await fetchApiKeys();
      }
    } catch (err) {
      console.error('Failed to regenerate API key:', err);
    }
  };

  // Create webhook
  const createWebhook = async () => {
    if (!newWebhookUrl.trim()) {
      setError('Please enter a webhook URL');
      return;
    }
    if (newWebhookEvents.length === 0) {
      setError('Please select at least one event');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/user-webhooks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          url: newWebhookUrl,
          events: newWebhookEvents,
        }),
      });

      const data: ApiResponse<CreateWebhookResponse> = await response.json();
      if (data.success) {
        setCreatedWebhookSecret(data.data.secret);
        await fetchWebhooks();
      } else {
        setError(data.error || 'Failed to create webhook');
      }
    } catch (err) {
      setError('Failed to create webhook');
    } finally {
      setCreating(false);
    }
  };

  // Delete webhook
  const deleteWebhook = async (webhookId: number) => {
    if (!confirm('Are you sure you want to delete this webhook?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/user-webhooks/${webhookId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data: ApiResponse<null> = await response.json();
      if (data.success) {
        await fetchWebhooks();
      }
    } catch (err) {
      console.error('Failed to delete webhook:', err);
    }
  };

  // Test webhook
  const testWebhook = async (webhookId: number) => {
    try {
      const response = await fetch(`${API_URL}/user-webhooks/${webhookId}/test`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data: ApiResponse<TestWebhookResponse> = await response.json();
      if (data.success && data.data.delivered) {
        alert('Test webhook delivered successfully!');
      } else {
        alert(`Webhook test failed: ${data.error || 'Unknown error'}`);
      }
      await fetchWebhooks();
    } catch (err) {
      console.error('Failed to test webhook:', err);
      alert('Failed to test webhook');
    }
  };

  // API Playground request
  const sendPlaygroundRequest = async () => {
    setPlaygroundLoading(true);
    setPlaygroundResponse(null);
    setPlaygroundStatus(null);

    try {
      const options: RequestInit = {
        method: playgroundMethod,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      };

      if (playgroundMethod !== 'GET' && playgroundBody.trim()) {
        options.body = playgroundBody;
      }

      const response = await fetch(`${API_URL}${playgroundPath.replace('/api', '')}`, options);
      const data = await response.json();

      setPlaygroundStatus(response.status);
      setPlaygroundResponse(JSON.stringify(data, null, 2));
    } catch (err) {
      setPlaygroundStatus(500);
      setPlaygroundResponse(JSON.stringify({ error: 'Request failed', message: String(err) }, null, 2));
    } finally {
      setPlaygroundLoading(false);
    }
  };

  // Reset create key modal
  const resetCreateKeyModal = () => {
    setShowCreateKey(false);
    setNewKeyName('');
    setNewKeyPermissions([]);
    setNewKeyExpiry(0);
    setCreatedKey(null);
    setError(null);
  };

  // Reset create webhook modal
  const resetCreateWebhookModal = () => {
    setShowCreateWebhook(false);
    setNewWebhookUrl('');
    setNewWebhookEvents([]);
    setCreatedWebhookSecret(null);
    setError(null);
  };

  // Open key documentation modal
  const openKeyDocs = (key: ApiKey) => {
    setSelectedKeyForDocs(key);
    setDocSection('overview');
    setShowKeyDocs(true);
  };

  // Download Postman collection for a key
  const downloadPostmanCollection = (key: ApiKey) => {
    const collection = generatePostmanCollection(key);
    const blob = new Blob([JSON.stringify(collection, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sweetpilot-${key.name.toLowerCase().replace(/\s+/g, '-')}.postman_collection.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Group permissions by category
  const groupedPermissions = permissions.reduce((acc, perm) => {
    if (!acc[perm.category]) {
      acc[perm.category] = [];
    }
    acc[perm.category].push(perm);
    return acc;
  }, {} as Record<string, Permission[]>);

  // Group webhook events by category
  const groupedEvents = webhookEvents.reduce((acc, event) => {
    if (!acc[event.category]) {
      acc[event.category] = [];
    }
    acc[event.category].push(event);
    return acc;
  }, {} as Record<string, WebhookEvent[]>);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-accent-purple" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>API & Developer Tools</h1>
          <p className={`mt-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Manage API keys, webhooks, and integrations</p>
        </div>
        <button
          onClick={() => setActiveTab('docs')}
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-colors ${isDark ? 'bg-dark-700 hover:bg-dark-600 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-900'}`}
        >
          <Book className="w-5 h-5" />
          Full Documentation
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* Usage Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Requests', value: stats.total_requests.toLocaleString(), icon: Activity, color: 'from-purple-500 to-pink-500' },
          { label: 'Active Keys', value: stats.active_keys.toString(), icon: Key, color: 'from-green-500 to-emerald-500' },
          { label: 'Total Keys', value: stats.total_keys.toString(), icon: Shield, color: 'from-blue-500 to-cyan-500' },
          { label: 'Webhooks', value: webhooks.length.toString(), icon: Webhook, color: 'from-yellow-500 to-orange-500' },
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`p-5 rounded-2xl border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{stat.label}</p>
                <p className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>{stat.value}</p>
              </div>
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                <stat.icon className="w-5 h-5 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Tabs */}
      <div className={`flex flex-wrap gap-2 border-b pb-4 ${isDark ? 'border-border' : 'border-slate-200'}`}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-gradient-to-r from-purple-500 to-cyan-500 text-white'
                : isDark ? 'text-text-muted hover:text-white hover:bg-dark-700' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'keys' && (
          <motion.div
            key="keys"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            <div className="flex justify-between items-center">
              <p className={isDark ? 'text-text-muted' : 'text-slate-500'}>Manage your API keys for programmatic access</p>
              <button
                onClick={() => setShowCreateKey(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl text-white font-medium hover:shadow-glow-sm transition-shadow"
              >
                <Plus className="w-4 h-4" />
                Create API Key
              </button>
            </div>

            {apiKeys.length === 0 ? (
              <div className={`text-center py-12 rounded-2xl border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200'}`}>
                <Key className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                <h3 className={`text-lg font-medium mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>No API Keys</h3>
                <p className={`mb-4 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Create your first API key to start using the API</p>
                <button
                  onClick={() => setShowCreateKey(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl text-white font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Create API Key
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {apiKeys.map((key) => (
                  <div key={key.id} className={`p-5 rounded-2xl border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          key.is_active ? 'bg-green-500/20' : 'bg-gray-500/20'
                        }`}>
                          <Key className={`w-5 h-5 ${key.is_active ? 'text-green-400' : 'text-gray-400'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{key.name}</h3>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              key.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                            }`}>
                              {key.is_active ? 'active' : 'inactive'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <code className={`text-sm font-mono px-3 py-1.5 rounded-lg ${isDark ? 'text-text-muted bg-dark-700' : 'text-slate-600 bg-slate-100'}`}>
                              {showApiKey[key.id] && key.key ? key.key : maskApiKey(key.key_prefix + '••••••••••••••••••••••••••')}
                            </code>
                            {key.key && (
                              <button
                                onClick={() => toggleKeyVisibility(key.id)}
                                className={`p-1.5 transition-colors ${isDark ? 'text-text-muted hover:text-white' : 'text-slate-400 hover:text-slate-600'}`}
                              >
                                {showApiKey[key.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            )}
                            <button
                              onClick={() => copyToClipboard(key.key || key.key_prefix, key.id)}
                              className={`p-1.5 transition-colors ${isDark ? 'text-text-muted hover:text-white' : 'text-slate-400 hover:text-slate-600'}`}
                            >
                              {copiedKey === key.id ? (
                                <CheckCircle className="w-4 h-4 text-green-400" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                          <div className={`flex items-center gap-4 mt-2 text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                            <span>Created: {new Date(key.created_at).toLocaleDateString()}</span>
                            <span>Last used: {formatDate(key.last_used_at)}</span>
                            <span>{key.request_count.toLocaleString()} requests</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openKeyDocs(key)}
                          className={`p-2 rounded-lg transition-colors ${isDark ? 'text-text-muted hover:text-accent-purple hover:bg-accent-purple/10' : 'text-slate-400 hover:text-purple-600 hover:bg-purple-50'}`}
                          title="View documentation"
                        >
                          <Book className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => downloadPostmanCollection(key)}
                          className={`p-2 rounded-lg transition-colors ${isDark ? 'text-text-muted hover:text-accent-cyan hover:bg-accent-cyan/10' : 'text-slate-400 hover:text-cyan-600 hover:bg-cyan-50'}`}
                          title="Download Postman collection"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => regenerateApiKey(key.id)}
                          className={`p-2 rounded-lg transition-colors ${isDark ? 'text-text-muted hover:text-white hover:bg-dark-600' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`}
                          title="Regenerate key"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteApiKey(key.id)}
                          className={`p-2 rounded-lg transition-colors ${isDark ? 'text-text-muted hover:text-red-400 hover:bg-red-500/10' : 'text-slate-400 hover:text-red-600 hover:bg-red-50'}`}
                          title="Delete key"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className={`mt-4 pt-4 border-t ${isDark ? 'border-border' : 'border-slate-200'}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Shield className={`w-4 h-4 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                        <span className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Permissions:</span>
                        {key.permissions.map((perm) => (
                          <span
                            key={perm}
                            className={`px-2 py-0.5 rounded text-xs ${isDark ? 'bg-dark-700 text-text-secondary' : 'bg-slate-100 text-slate-600'}`}
                          >
                            {perm}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'webhooks' && (
          <motion.div
            key="webhooks"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            <div className="flex justify-between items-center">
              <p className={isDark ? 'text-text-muted' : 'text-slate-500'}>Receive real-time notifications when events occur</p>
              <button
                onClick={() => setShowCreateWebhook(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl text-white font-medium hover:shadow-glow-sm transition-shadow"
              >
                <Plus className="w-4 h-4" />
                Add Webhook
              </button>
            </div>

            {webhooks.length === 0 ? (
              <div className={`text-center py-12 rounded-2xl border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200'}`}>
                <Webhook className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                <h3 className={`text-lg font-medium mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>No Webhooks</h3>
                <p className={`mb-4 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Add a webhook to receive real-time notifications</p>
                <button
                  onClick={() => setShowCreateWebhook(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl text-white font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Add Webhook
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {webhooks.map((webhook) => (
                  <div key={webhook.id} className={`p-5 rounded-2xl border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          webhook.is_active ? 'bg-green-500/20' : 'bg-gray-500/20'
                        }`}>
                          <Webhook className={`w-5 h-5 ${webhook.is_active ? 'text-green-400' : 'text-gray-400'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <code className={`font-mono text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>{webhook.url}</code>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              webhook.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                            }`}>
                              {webhook.is_active ? 'active' : 'inactive'}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {webhook.events.map((event) => (
                              <span
                                key={event}
                                className={`px-2 py-1 rounded-lg text-xs font-mono ${isDark ? 'bg-dark-700 text-text-secondary' : 'bg-slate-100 text-slate-600'}`}
                              >
                                {event}
                              </span>
                            ))}
                          </div>
                          <div className={`flex items-center gap-4 mt-2 text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                            <span>Last triggered: {formatDate(webhook.last_triggered_at)}</span>
                            <span className="text-green-400">{webhook.success_rate}% success rate</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => testWebhook(webhook.id)}
                          className={`p-2 rounded-lg transition-colors ${isDark ? 'text-text-muted hover:text-white hover:bg-dark-600' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`}
                          title="Test webhook"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteWebhook(webhook.id)}
                          className={`p-2 rounded-lg transition-colors ${isDark ? 'text-text-muted hover:text-red-400 hover:bg-red-500/10' : 'text-slate-400 hover:text-red-600 hover:bg-red-50'}`}
                          title="Delete webhook"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Webhook Events Reference */}
            <div className={`p-6 rounded-2xl border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Available Events</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {webhookEvents.map((item) => (
                  <div key={item.id} className={`p-3 rounded-lg ${isDark ? 'bg-dark-700/50' : 'bg-slate-50'}`}>
                    <code className="text-sm text-accent-purple font-mono">{item.id}</code>
                    <p className={`text-xs mt-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === 'docs' && (
          <motion.div
            key="docs"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-6"
          >
            {/* Documentation Header */}
            <div className={`p-6 rounded-2xl border ${isDark ? 'bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border-purple-500/20' : 'bg-gradient-to-br from-purple-50 to-cyan-50 border-purple-200'}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>SweetPilot API Documentation</h2>
                  <p className={isDark ? 'text-text-secondary' : 'text-slate-600'}>
                    Complete reference for integrating with the SweetPilot API. Build powerful automations with our AI employees.
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      if (apiKeys.length > 0) {
                        downloadPostmanCollection(apiKeys[0]);
                      } else {
                        alert('Create an API key first to download a Postman collection');
                      }
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl text-white font-medium hover:shadow-glow-sm transition-shadow"
                  >
                    <Download className="w-4 h-4" />
                    Postman Collection
                  </button>
                </div>
              </div>
            </div>

            {/* Base URL & Authentication */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className={`p-6 rounded-2xl border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                    <Zap className="w-5 h-5 text-purple-500" />
                  </div>
                  <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Base URL</h3>
                </div>
                <div className={`p-4 rounded-xl font-mono text-sm ${isDark ? 'bg-dark-900 text-green-400' : 'bg-slate-900 text-green-400'}`}>
                  {fullApiDocumentation.baseUrl}
                </div>
              </div>

              <div className={`p-6 rounded-2xl border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-cyan-500/20' : 'bg-cyan-100'}`}>
                    <Key className="w-5 h-5 text-cyan-500" />
                  </div>
                  <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{fullApiDocumentation.authentication.title}</h3>
                </div>
                <p className={`text-sm mb-3 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{fullApiDocumentation.authentication.description}</p>
                <div className={`p-4 rounded-xl font-mono text-sm ${isDark ? 'bg-dark-900 text-green-400' : 'bg-slate-900 text-green-400'}`}>
                  {fullApiDocumentation.authentication.example}
                </div>
                <ul className="mt-4 space-y-2">
                  {fullApiDocumentation.authentication.notes.map((note, i) => (
                    <li key={i} className={`flex items-start gap-2 text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                      <Info className="w-4 h-4 mt-0.5 text-cyan-500 flex-shrink-0" />
                      {note}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Rate Limiting */}
            <div className={`p-6 rounded-2xl border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-yellow-500/20' : 'bg-yellow-100'}`}>
                  <Activity className="w-5 h-5 text-yellow-500" />
                </div>
                <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{fullApiDocumentation.rateLimiting.title}</h3>
              </div>
              <p className={`text-sm mb-4 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{fullApiDocumentation.rateLimiting.description}</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {fullApiDocumentation.rateLimiting.limits.map((limit) => (
                  <div key={limit.plan} className={`p-4 rounded-xl ${isDark ? 'bg-dark-700/50' : 'bg-slate-50'}`}>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{limit.plan}</p>
                    <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{limit.requests}</p>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <p className={`text-sm font-medium ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>Response Headers:</p>
                {fullApiDocumentation.rateLimiting.headers.map((header) => (
                  <div key={header.name} className="flex items-center gap-2">
                    <code className={`text-xs px-2 py-1 rounded ${isDark ? 'bg-dark-700 text-accent-purple' : 'bg-slate-100 text-purple-600'}`}>{header.name}</code>
                    <span className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{header.description}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* API Endpoints by Category */}
            <div className="space-y-4">
              <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>API Endpoints</h3>
              {fullApiDocumentation.endpoints.map((category, categoryIndex) => (
                <div key={category.category} className={`rounded-2xl border overflow-hidden ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
                  <div className={`px-6 py-4 border-b ${isDark ? 'bg-dark-700/50 border-border' : 'bg-slate-50 border-slate-200'}`}>
                    <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{category.category}</h4>
                  </div>
                  <div className="divide-y divide-border">
                    {category.endpoints.map((endpoint, endpointIndex) => {
                      const uniqueKey = categoryIndex * 100 + endpointIndex;
                      return (
                        <div key={endpoint.path + endpoint.method}>
                          <button
                            onClick={() => setExpandedEndpoint(expandedEndpoint === uniqueKey ? null : uniqueKey)}
                            className={`w-full flex items-center justify-between p-4 transition-colors ${isDark ? 'hover:bg-dark-700/30' : 'hover:bg-slate-50'}`}
                          >
                            <div className="flex items-center gap-3">
                              <span className={`px-2.5 py-1 rounded-lg text-xs font-bold ${getMethodColor(endpoint.method)}`}>
                                {endpoint.method}
                              </span>
                              <code className={`font-mono text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>{endpoint.path}</code>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={`text-sm hidden sm:block ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{endpoint.description}</span>
                              <ChevronDown
                                className={`w-4 h-4 transition-transform ${isDark ? 'text-text-muted' : 'text-slate-400'} ${
                                  expandedEndpoint === uniqueKey ? 'rotate-180' : ''
                                }`}
                              />
                            </div>
                          </button>

                          <AnimatePresence>
                            {expandedEndpoint === uniqueKey && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className={`border-t overflow-hidden ${isDark ? 'border-border' : 'border-slate-200'}`}
                              >
                                <div className={`p-6 ${isDark ? 'bg-dark-700/20' : 'bg-slate-50'}`}>
                                  {/* Permissions */}
                                  <div className="mb-4">
                                    <p className={`text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>Required Permissions:</p>
                                    <div className="flex gap-2">
                                      {endpoint.permissions.map((perm) => (
                                        <span key={perm} className={`px-2 py-1 rounded text-xs ${isDark ? 'bg-accent-purple/20 text-accent-purple' : 'bg-purple-100 text-purple-600'}`}>
                                          {perm}
                                        </span>
                                      ))}
                                    </div>
                                  </div>

                                  {/* Parameters */}
                                  {endpoint.parameters.length > 0 && (
                                    <div className="mb-4">
                                      <p className={`text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>Parameters:</p>
                                      <div className="space-y-2">
                                        {endpoint.parameters.map((param) => (
                                          <div key={param.name} className={`flex items-start gap-3 p-3 rounded-lg ${isDark ? 'bg-dark-700/50' : 'bg-white'}`}>
                                            <code className={`text-sm font-mono ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`}>{param.name}</code>
                                            <span className={`text-xs px-1.5 py-0.5 rounded ${isDark ? 'bg-dark-600 text-text-muted' : 'bg-slate-200 text-slate-600'}`}>{param.type}</span>
                                            {param.required && (
                                              <span className="text-xs px-1.5 py-0.5 rounded bg-red-500/20 text-red-400">required</span>
                                            )}
                                            <span className={`text-sm flex-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{param.description}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Example Response */}
                                  <div>
                                    <div className="flex items-center justify-between mb-2">
                                      <p className={`text-sm font-medium ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>Example Response:</p>
                                      <button
                                        onClick={() => copyToClipboard(endpoint.response, 2000 + uniqueKey)}
                                        className={`text-sm flex items-center gap-1 ${isDark ? 'text-text-muted hover:text-white' : 'text-slate-400 hover:text-slate-600'}`}
                                      >
                                        {copiedKey === 2000 + uniqueKey ? (
                                          <>
                                            <CheckCircle className="w-4 h-4 text-green-400" />
                                            Copied!
                                          </>
                                        ) : (
                                          <>
                                            <Copy className="w-4 h-4" />
                                            Copy
                                          </>
                                        )}
                                      </button>
                                    </div>
                                    <pre className={`p-4 rounded-xl overflow-x-auto ${isDark ? 'bg-dark-900' : 'bg-slate-900'}`}>
                                      <code className="text-sm text-green-400 font-mono whitespace-pre">
                                        {endpoint.response}
                                      </code>
                                    </pre>
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Error Codes */}
            <div className={`p-6 rounded-2xl border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-red-500/20' : 'bg-red-100'}`}>
                  <AlertCircle className="w-5 h-5 text-red-500" />
                </div>
                <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Error Codes</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={`border-b ${isDark ? 'border-border' : 'border-slate-200'}`}>
                      <th className={`text-left py-3 px-4 font-medium ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>Code</th>
                      <th className={`text-left py-3 px-4 font-medium ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>Name</th>
                      <th className={`text-left py-3 px-4 font-medium ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fullApiDocumentation.errorCodes.map((error) => (
                      <tr key={error.code} className={`border-b ${isDark ? 'border-border/50' : 'border-slate-100'}`}>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded text-xs font-mono font-bold ${
                            error.code < 500 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'
                          }`}>
                            {error.code}
                          </span>
                        </td>
                        <td className={`py-3 px-4 font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{error.name}</td>
                        <td className={`py-3 px-4 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{error.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* SDK Downloads */}
            <div className={`p-6 rounded-2xl border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Official SDKs</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { name: 'JavaScript/Node.js', icon: '🟨', command: 'npm install @sweetpilot/sdk' },
                  { name: 'Python', icon: '🐍', command: 'pip install sweetpilot' },
                  { name: 'Ruby', icon: '💎', command: 'gem install sweetpilot' },
                  { name: 'Go', icon: '🐹', command: 'go get github.com/sweetpilot/go-sdk' },
                ].map((sdk) => (
                  <div key={sdk.name} className={`p-4 rounded-xl ${isDark ? 'bg-dark-700/50' : 'bg-slate-50'}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-2xl">{sdk.icon}</span>
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{sdk.name}</span>
                    </div>
                    <code className={`text-xs font-mono px-2 py-1 rounded block ${isDark ? 'text-text-muted bg-dark-800' : 'text-slate-600 bg-slate-200'}`}>
                      {sdk.command}
                    </code>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Start Examples */}
            <div className={`p-6 rounded-2xl border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Quick Start Examples</h3>
              <div className="space-y-4">
                {apiEndpoints.map((endpoint, index) => (
                  <div key={index} className={`rounded-xl overflow-hidden ${isDark ? 'bg-dark-700/50' : 'bg-slate-50'}`}>
                    <div className={`flex items-center justify-between px-4 py-2 ${isDark ? 'bg-dark-700' : 'bg-slate-100'}`}>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${getMethodColor(endpoint.method)}`}>
                          {endpoint.method}
                        </span>
                        <span className={`text-sm ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>{endpoint.description}</span>
                      </div>
                      <button
                        onClick={() => copyToClipboard(endpoint.example, 3000 + index)}
                        className={`text-sm flex items-center gap-1 ${isDark ? 'text-text-muted hover:text-white' : 'text-slate-400 hover:text-slate-600'}`}
                      >
                        {copiedKey === 3000 + index ? (
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    <pre className={`p-4 overflow-x-auto ${isDark ? 'bg-dark-900' : 'bg-slate-900'}`}>
                      <code className="text-sm text-green-400 font-mono whitespace-pre">
                        {endpoint.example}
                      </code>
                    </pre>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === 'playground' && (
          <motion.div
            key="playground"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            <p className={isDark ? 'text-text-muted' : 'text-slate-500'}>Test API endpoints directly in your browser</p>

            <div className={`p-6 rounded-2xl border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Request */}
                <div>
                  <h3 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Request</h3>

                  <div className="space-y-4">
                    <div className="flex gap-3">
                      <select
                        value={playgroundMethod}
                        onChange={(e) => setPlaygroundMethod(e.target.value)}
                        className={`px-4 py-2.5 border rounded-xl focus:outline-none focus:border-accent-purple ${isDark ? 'bg-dark-700 border-border text-white' : 'bg-slate-50 border-slate-200 text-slate-900'}`}
                      >
                        <option value="GET">GET</option>
                        <option value="POST">POST</option>
                        <option value="PUT">PUT</option>
                        <option value="DELETE">DELETE</option>
                      </select>
                      <input
                        type="text"
                        value={playgroundPath}
                        onChange={(e) => setPlaygroundPath(e.target.value)}
                        className={`flex-1 px-4 py-2.5 border rounded-xl font-mono text-sm focus:outline-none focus:border-accent-purple ${isDark ? 'bg-dark-700 border-border text-white placeholder-text-muted' : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'}`}
                      />
                    </div>

                    <div>
                      <label className={`block text-sm mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>Body (JSON)</label>
                      <textarea
                        rows={6}
                        value={playgroundBody}
                        onChange={(e) => setPlaygroundBody(e.target.value)}
                        className={`w-full px-4 py-3 border rounded-xl font-mono text-sm resize-none focus:outline-none focus:border-accent-purple ${isDark ? 'bg-dark-700 border-border text-white placeholder-text-muted' : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'}`}
                      />
                    </div>

                    <button
                      onClick={sendPlaygroundRequest}
                      disabled={playgroundLoading}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl text-white font-medium hover:shadow-glow-sm transition-shadow disabled:opacity-50"
                    >
                      {playgroundLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Play className="w-5 h-5" />
                      )}
                      {playgroundLoading ? 'Sending...' : 'Send Request'}
                    </button>
                  </div>
                </div>

                {/* Response */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Response</h3>
                    {playgroundStatus && (
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        playgroundStatus < 300 ? 'bg-green-500/20 text-green-400' :
                        playgroundStatus < 400 ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {playgroundStatus} {playgroundStatus < 300 ? 'OK' : playgroundStatus < 400 ? 'Redirect' : 'Error'}
                      </span>
                    )}
                  </div>

                  <div className={`rounded-xl p-4 h-[400px] overflow-auto ${isDark ? 'bg-dark-900' : 'bg-slate-900'}`}>
                    <pre className="text-sm text-green-400 font-mono">
                      {playgroundResponse || '// Response will appear here'}
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create API Key Modal */}
      <AnimatePresence>
        {showCreateKey && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={resetCreateKeyModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className={`w-full max-w-lg rounded-2xl border p-6 max-h-[90vh] overflow-y-auto ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200'}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {createdKey ? 'API Key Created' : 'Create API Key'}
                </h2>
                <button onClick={resetCreateKeyModal} className={isDark ? 'text-text-muted hover:text-white' : 'text-slate-400 hover:text-slate-600'}>
                  <X className="w-5 h-5" />
                </button>
              </div>

              {createdKey ? (
                <div className="space-y-4">
                  <div className={`p-4 rounded-xl ${isDark ? 'bg-green-500/10 border border-green-500/20' : 'bg-green-50 border border-green-200'}`}>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                      <div>
                        <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Your API key has been created!</p>
                        <p className={`text-sm mt-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                          Copy this key now. It will not be shown again.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>Your API Key</label>
                    <div className="flex gap-2">
                      <code className={`flex-1 px-4 py-3 rounded-xl font-mono text-sm break-all ${isDark ? 'bg-dark-700 text-white' : 'bg-slate-100 text-slate-900'}`}>
                        {createdKey}
                      </code>
                      <button
                        onClick={() => copyToClipboard(createdKey, -1)}
                        className="px-4 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl text-white font-medium"
                      >
                        {copiedKey === -1 ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={resetCreateKeyModal}
                    className="w-full px-4 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl text-white font-medium"
                  >
                    Done
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {error && (
                    <div className={`p-3 rounded-xl text-sm ${isDark ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-red-50 text-red-600 border border-red-200'}`}>
                      {error}
                    </div>
                  )}

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>Key Name</label>
                    <input
                      type="text"
                      placeholder="e.g., Production API Key"
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                      className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:border-accent-purple ${isDark ? 'bg-dark-700 border-border text-white placeholder-text-muted' : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'}`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>Expiration (optional)</label>
                    <select
                      value={newKeyExpiry}
                      onChange={(e) => setNewKeyExpiry(parseInt(e.target.value))}
                      className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:border-accent-purple ${isDark ? 'bg-dark-700 border-border text-white' : 'bg-slate-50 border-slate-200 text-slate-900'}`}
                    >
                      <option value={0}>Never expires</option>
                      <option value={30}>30 days</option>
                      <option value={60}>60 days</option>
                      <option value={90}>90 days</option>
                      <option value={180}>180 days</option>
                      <option value={365}>1 year</option>
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>Permissions</label>
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {Object.entries(groupedPermissions).map(([category, perms]) => (
                        <div key={category}>
                          <p className={`text-xs font-medium mb-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{category}</p>
                          <div className="space-y-1">
                            {perms.map((perm) => (
                              <label
                                key={perm.id}
                                className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${isDark ? 'hover:bg-dark-600' : 'hover:bg-slate-50'}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={newKeyPermissions.includes(perm.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setNewKeyPermissions([...newKeyPermissions, perm.id]);
                                    } else {
                                      setNewKeyPermissions(newKeyPermissions.filter(p => p !== perm.id));
                                    }
                                  }}
                                  className="w-4 h-4 rounded border-border bg-dark-600 text-accent-purple focus:ring-accent-purple"
                                />
                                <div className="flex-1">
                                  <span className={isDark ? 'text-white' : 'text-slate-900'}>{perm.name}</span>
                                  <span className={`text-xs ml-2 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{perm.description}</span>
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className={`p-4 rounded-xl text-sm ${isDark ? 'bg-dark-700/50 text-text-muted' : 'bg-slate-50 text-slate-500'}`}>
                    <AlertCircle className="w-4 h-4 inline mr-2 text-yellow-400" />
                    The API key will only be shown once. Make sure to copy it to a secure location.
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={resetCreateKeyModal}
                      className={`flex-1 px-4 py-3 rounded-xl font-medium transition-colors ${isDark ? 'bg-dark-700 hover:bg-dark-600 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-900'}`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={createApiKey}
                      disabled={creating}
                      className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl text-white font-medium hover:shadow-glow-sm transition-shadow disabled:opacity-50"
                    >
                      {creating ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Create Key'}
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Webhook Modal */}
      <AnimatePresence>
        {showCreateWebhook && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={resetCreateWebhookModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className={`w-full max-w-lg rounded-2xl border p-6 max-h-[90vh] overflow-y-auto ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200'}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {createdWebhookSecret ? 'Webhook Created' : 'Add Webhook'}
                </h2>
                <button onClick={resetCreateWebhookModal} className={isDark ? 'text-text-muted hover:text-white' : 'text-slate-400 hover:text-slate-600'}>
                  <X className="w-5 h-5" />
                </button>
              </div>

              {createdWebhookSecret ? (
                <div className="space-y-4">
                  <div className={`p-4 rounded-xl ${isDark ? 'bg-green-500/10 border border-green-500/20' : 'bg-green-50 border border-green-200'}`}>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                      <div>
                        <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Your webhook has been created!</p>
                        <p className={`text-sm mt-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                          Copy this secret now. It will not be shown again.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>Webhook Signing Secret</label>
                    <div className="flex gap-2">
                      <code className={`flex-1 px-4 py-3 rounded-xl font-mono text-sm break-all ${isDark ? 'bg-dark-700 text-white' : 'bg-slate-100 text-slate-900'}`}>
                        {createdWebhookSecret}
                      </code>
                      <button
                        onClick={() => copyToClipboard(createdWebhookSecret, -2)}
                        className="px-4 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl text-white font-medium"
                      >
                        {copiedKey === -2 ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={resetCreateWebhookModal}
                    className="w-full px-4 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl text-white font-medium"
                  >
                    Done
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {error && (
                    <div className={`p-3 rounded-xl text-sm ${isDark ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-red-50 text-red-600 border border-red-200'}`}>
                      {error}
                    </div>
                  )}

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>Endpoint URL</label>
                    <input
                      type="url"
                      placeholder="https://your-app.com/webhooks/sweetpilot"
                      value={newWebhookUrl}
                      onChange={(e) => setNewWebhookUrl(e.target.value)}
                      className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:border-accent-purple ${isDark ? 'bg-dark-700 border-border text-white placeholder-text-muted' : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'}`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>Events to Subscribe</label>
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {Object.entries(groupedEvents).map(([category, events]) => (
                        <div key={category}>
                          <p className={`text-xs font-medium mb-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{category}</p>
                          <div className="grid grid-cols-2 gap-1">
                            {events.map((event) => (
                              <label
                                key={event.id}
                                className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${isDark ? 'hover:bg-dark-600' : 'hover:bg-slate-50'}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={newWebhookEvents.includes(event.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setNewWebhookEvents([...newWebhookEvents, event.id]);
                                    } else {
                                      setNewWebhookEvents(newWebhookEvents.filter(ev => ev !== event.id));
                                    }
                                  }}
                                  className="w-4 h-4 rounded border-border bg-dark-600 text-accent-purple focus:ring-accent-purple"
                                />
                                <code className={`text-xs ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>{event.id}</code>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={resetCreateWebhookModal}
                      className={`flex-1 px-4 py-3 rounded-xl font-medium transition-colors ${isDark ? 'bg-dark-700 hover:bg-dark-600 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-900'}`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={createWebhook}
                      disabled={creating}
                      className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl text-white font-medium hover:shadow-glow-sm transition-shadow disabled:opacity-50"
                    >
                      {creating ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Add Webhook'}
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Per-API Key Documentation Modal */}
      <AnimatePresence>
        {showKeyDocs && selectedKeyForDocs && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowKeyDocs(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className={`w-full max-w-4xl rounded-2xl border max-h-[90vh] overflow-hidden flex flex-col ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200'}`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className={`flex items-center justify-between p-6 border-b ${isDark ? 'border-border' : 'border-slate-200'}`}>
                <div>
                  <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    API Key Documentation
                  </h2>
                  <p className={`text-sm mt-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                    {selectedKeyForDocs.name} - {selectedKeyForDocs.key_prefix}••••••••
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => downloadPostmanCollection(selectedKeyForDocs)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl text-white font-medium text-sm"
                  >
                    <Download className="w-4 h-4" />
                    Postman
                  </button>
                  <button
                    onClick={() => setShowKeyDocs(false)}
                    className={isDark ? 'text-text-muted hover:text-white' : 'text-slate-400 hover:text-slate-600'}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Modal Nav */}
              <div className={`flex gap-2 px-6 py-3 border-b ${isDark ? 'border-border bg-dark-700/30' : 'border-slate-200 bg-slate-50'}`}>
                {[
                  { id: 'overview' as const, label: 'Overview', icon: Info },
                  { id: 'endpoints' as const, label: 'Endpoints', icon: Code },
                  { id: 'examples' as const, label: 'Examples', icon: Terminal },
                  { id: 'postman' as const, label: 'Postman', icon: FileJson },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setDocSection(tab.id)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      docSection === tab.id
                        ? 'bg-gradient-to-r from-purple-500 to-cyan-500 text-white'
                        : isDark ? 'text-text-muted hover:text-white hover:bg-dark-600' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Modal Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {docSection === 'overview' && (
                  <div className="space-y-6">
                    {/* Key Info */}
                    <div className={`p-4 rounded-xl ${isDark ? 'bg-dark-700/50' : 'bg-slate-50'}`}>
                      <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>API Key Information</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Name</p>
                          <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{selectedKeyForDocs.name}</p>
                        </div>
                        <div>
                          <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Status</p>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            selectedKeyForDocs.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                          }`}>
                            {selectedKeyForDocs.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div>
                          <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Created</p>
                          <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{new Date(selectedKeyForDocs.created_at).toLocaleDateString()}</p>
                        </div>
                        <div>
                          <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Requests</p>
                          <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{selectedKeyForDocs.request_count.toLocaleString()}</p>
                        </div>
                      </div>
                    </div>

                    {/* Permissions */}
                    <div>
                      <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>Permissions</h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedKeyForDocs.permissions.map((perm) => (
                          <span key={perm} className={`px-3 py-1.5 rounded-lg text-sm ${isDark ? 'bg-accent-purple/20 text-accent-purple' : 'bg-purple-100 text-purple-600'}`}>
                            {perm}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Authentication */}
                    <div>
                      <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>Authentication</h3>
                      <p className={`text-sm mb-3 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                        Include this header in all API requests:
                      </p>
                      <div className={`p-4 rounded-xl font-mono text-sm ${isDark ? 'bg-dark-900' : 'bg-slate-900'}`}>
                        <code className="text-green-400">Authorization: Bearer {selectedKeyForDocs.key || selectedKeyForDocs.key_prefix + '••••••••••••••••'}</code>
                      </div>
                    </div>

                    {/* Base URL */}
                    <div>
                      <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>Base URL</h3>
                      <div className={`p-4 rounded-xl font-mono text-sm ${isDark ? 'bg-dark-900' : 'bg-slate-900'}`}>
                        <code className="text-green-400">{fullApiDocumentation.baseUrl}</code>
                      </div>
                    </div>
                  </div>
                )}

                {docSection === 'endpoints' && (
                  <div className="space-y-4">
                    <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                      Based on your API key permissions, you have access to the following endpoints:
                    </p>
                    {fullApiDocumentation.endpoints.map((category) => {
                      const accessibleEndpoints = category.endpoints.filter(endpoint =>
                        endpoint.permissions.some(perm => selectedKeyForDocs.permissions.includes(perm))
                      );
                      if (accessibleEndpoints.length === 0) return null;
                      return (
                        <div key={category.category} className={`rounded-xl border overflow-hidden ${isDark ? 'bg-dark-700/30 border-border' : 'bg-slate-50 border-slate-200'}`}>
                          <div className={`px-4 py-3 border-b ${isDark ? 'bg-dark-700/50 border-border' : 'bg-slate-100 border-slate-200'}`}>
                            <h4 className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{category.category}</h4>
                          </div>
                          <div className="p-4 space-y-2">
                            {accessibleEndpoints.map((endpoint) => (
                              <div key={endpoint.path + endpoint.method} className={`flex items-center gap-3 p-3 rounded-lg ${isDark ? 'bg-dark-800' : 'bg-white'}`}>
                                <span className={`px-2 py-0.5 rounded text-xs font-bold ${getMethodColor(endpoint.method)}`}>
                                  {endpoint.method}
                                </span>
                                <code className={`font-mono text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>{endpoint.path}</code>
                                <span className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{endpoint.description}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {docSection === 'examples' && (
                  <div className="space-y-6">
                    <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                      Ready-to-use code examples with your API key:
                    </p>

                    {/* cURL Example */}
                    <div>
                      <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>cURL</h3>
                      <pre className={`p-4 rounded-xl overflow-x-auto ${isDark ? 'bg-dark-900' : 'bg-slate-900'}`}>
                        <code className="text-sm text-green-400 font-mono whitespace-pre">{`curl -X GET "${fullApiDocumentation.baseUrl}/employees" \\
  -H "Authorization: Bearer ${selectedKeyForDocs.key || selectedKeyForDocs.key_prefix + '••••••••••••••••'}" \\
  -H "Content-Type: application/json"`}</code>
                      </pre>
                    </div>

                    {/* JavaScript Example */}
                    <div>
                      <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>JavaScript / Node.js</h3>
                      <pre className={`p-4 rounded-xl overflow-x-auto ${isDark ? 'bg-dark-900' : 'bg-slate-900'}`}>
                        <code className="text-sm text-green-400 font-mono whitespace-pre">{`const response = await fetch('${fullApiDocumentation.baseUrl}/employees', {
  headers: {
    'Authorization': 'Bearer ${selectedKeyForDocs.key || selectedKeyForDocs.key_prefix + '••••••••••••••••'}',
    'Content-Type': 'application/json'
  }
});

const data = await response.json();
console.log(data);`}</code>
                      </pre>
                    </div>

                    {/* Python Example */}
                    <div>
                      <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>Python</h3>
                      <pre className={`p-4 rounded-xl overflow-x-auto ${isDark ? 'bg-dark-900' : 'bg-slate-900'}`}>
                        <code className="text-sm text-green-400 font-mono whitespace-pre">{`import requests

headers = {
    'Authorization': 'Bearer ${selectedKeyForDocs.key || selectedKeyForDocs.key_prefix + '••••••••••••••••'}',
    'Content-Type': 'application/json'
}

response = requests.get('${fullApiDocumentation.baseUrl}/employees', headers=headers)
data = response.json()
print(data)`}</code>
                      </pre>
                    </div>

                    {/* Send Message Example */}
                    <div>
                      <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>Send a Message to AI Employee</h3>
                      <pre className={`p-4 rounded-xl overflow-x-auto ${isDark ? 'bg-dark-900' : 'bg-slate-900'}`}>
                        <code className="text-sm text-green-400 font-mono whitespace-pre">{`// 1. Create a conversation
const conv = await fetch('${fullApiDocumentation.baseUrl}/conversations', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ${selectedKeyForDocs.key || selectedKeyForDocs.key_prefix + '••••••••••••••••'}',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    employee_id: 'aria',
    title: 'Social Media Campaign'
  })
});
const { data: conversation } = await conv.json();

// 2. Send a message
const msg = await fetch(\`${fullApiDocumentation.baseUrl}/conversations/\${conversation.id}/messages\`, {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ${selectedKeyForDocs.key || selectedKeyForDocs.key_prefix + '••••••••••••••••'}',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    content: 'Create a social media post about our new product launch'
  })
});
const { data: message } = await msg.json();
console.log(message.content);`}</code>
                      </pre>
                    </div>
                  </div>
                )}

                {docSection === 'postman' && (
                  <div className="space-y-6">
                    <div className={`p-6 rounded-xl text-center ${isDark ? 'bg-dark-700/50' : 'bg-slate-50'}`}>
                      <FileJson className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-accent-purple' : 'text-purple-500'}`} />
                      <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Download Postman Collection</h3>
                      <p className={`text-sm mb-4 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                        Get a ready-to-import Postman collection with all API endpoints pre-configured with your API key.
                      </p>
                      <button
                        onClick={() => downloadPostmanCollection(selectedKeyForDocs)}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl text-white font-medium hover:shadow-glow-sm transition-shadow"
                      >
                        <Download className="w-5 h-5" />
                        Download Collection
                      </button>
                    </div>

                    <div>
                      <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>How to Import</h3>
                      <ol className={`space-y-3 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                        <li className="flex items-start gap-3">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${isDark ? 'bg-accent-purple/20 text-accent-purple' : 'bg-purple-100 text-purple-600'}`}>1</span>
                          <span>Open Postman and click <strong>Import</strong> in the top left</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${isDark ? 'bg-accent-purple/20 text-accent-purple' : 'bg-purple-100 text-purple-600'}`}>2</span>
                          <span>Drag and drop the downloaded JSON file or click <strong>Upload Files</strong></span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${isDark ? 'bg-accent-purple/20 text-accent-purple' : 'bg-purple-100 text-purple-600'}`}>3</span>
                          <span>Click <strong>Import</strong> to add the collection to your workspace</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${isDark ? 'bg-accent-purple/20 text-accent-purple' : 'bg-purple-100 text-purple-600'}`}>4</span>
                          <span>Start making requests! Your API key is already configured</span>
                        </li>
                      </ol>
                    </div>

                    <div className={`p-4 rounded-xl ${isDark ? 'bg-cyan-500/10 border border-cyan-500/20' : 'bg-cyan-50 border border-cyan-200'}`}>
                      <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-cyan-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Tip: Environment Variables</p>
                          <p className={`text-sm mt-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                            The collection uses a <code className="px-1 py-0.5 rounded bg-slate-800 text-cyan-400">{'{{baseUrl}}'}</code> variable.
                            You can create a Postman environment to easily switch between production and development.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
